<!--
Author: Qoder
Date: 2026-03-10
Desc: 微信扫码上传证件信息组件（PC 端）
 -->
<template>
    <div class="wxscan-upload comp">
        <div class="wxscan-content">
            <!-- 操作步骤说明 -->
            <div class="steps-box">
                <div class="step-item">
                    <div class="step-text">1.打开微信选择扫一扫</div>
                    <div class="step-img-container" style="width: 180px">
                        <img
                            class="step-img"
                            src="@/assets/img/wx-tip.png"
                            alt="打开微信选择扫一扫"
                        />
                    </div>
                </div>
                <div class="step-item">
                    <div class="step-text">2.扫描二维码进入上传页面</div>
                    <div class="step-img-container">
                        <!-- 二维码展示区 -->
                        <div v-if="!initError" class="qr-section">
                            <div v-if="!isExpired" class="qr-code-container">
                                <div ref="qrCodeContainer" class="qr-code-box"></div>
                                <div v-if="checking" class="qr-mask">
                                    <i class="el-icon-loading"></i>
                                    <span>正在查询上传状态...</span>
                                </div>
                            </div>

                            <!-- 过期遮罩 -->
                            <div v-if="isExpired" class="qr-expired-mask">
                                <i class="el-icon-warning-outline"></i>
                                <p>二维码已过期</p>
                                <el-button type="primary" size="small" v-on:click="refreshQrCode">
                                    刷新二维码
                                </el-button>
                            </div>

                            <!-- 状态提示 -->
                            <div v-if="statusMessage" class="status-message">
                                <i class="el-icon-info"></i>
                                <span>{{ statusMessage }}</span>
                            </div>
                        </div>

                        <!-- 初始化失败提示 -->
                        <div v-else class="init-error">
                            <i class="el-icon-circle-close"></i>
                            <p v-if="statusMessage" style="text-align: center">
                                {{ statusMessage }}
                            </p>
                            <el-button type="primary" size="small" v-on:click="retryInit">
                                重试
                            </el-button>
                        </div>
                    </div>
                </div>
                <div class="step-item">
                    <div class="step-text" style="margin-bottom: 15px">3.上传证件</div>
                    <div class="step-img-container">
                        <img
                            class="step-img"
                            src="@/assets/img/wx-upload-phone.png"
                            alt="上传证件"
                        />
                    </div>
                </div>
            </div>
        </div>

        <!-- 按钮区 -->
        <div class="btn-group">
            <el-button
                type="primary"
                class="medium confirm"
                :loading="checking"
                :disabled="confirmDisabled"
                v-on:click="checkStatus"
            >
                已完成微信上传，点此继续操作
            </el-button>
        </div>
    </div>
</template>

<script>
import userApi from "@/api/user";
import commonApi from "@/api/common";
import QRCode from "qrcodejs2";

export default {
    name: "WxScanUpload",
    props: {
        certType: {
            type: String,
            default: "00"
        },
        isShow: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            uploadKey: "",
            qrCodeUrl: "",
            checking: false,
            createTime: 0,
            isExpired: false,
            statusMessage: "",
            initError: false,
            checkTimer: null,
            ocrResult: null,
            fileInfoList: []
        };
    },
    computed: {
        confirmDisabled() {
            return this.checking;
        }
    },
    watch: {
        isShow(val) {
            if (val) {
                this.initSession();
            }
        }
    },
    watch: {
        isShow(val) {
            if (val) {
                this.initSession();
            }
        }
    },
    mounted() {
        this.initSession();
    },
    beforeDestroy() {
        if (this._checkTimer) clearTimeout(this._checkTimer);
    },
    methods: {
        async initSession() {
            this.initError = false;
            try {
                const res = await commonApi.wechatCertUploadKey();
                if (res.code === "0" && res.data) {
                    this.uploadKey = res.data.uploadkey;
                    this.createTime = Date.now();
                    this.isExpired = false;
                    // 拼接 H5 访问 URL
                    const uploadPath = this.$cache.getCompanyInfoParam("10107005");
                    if (this.$util.isEmpty(uploadPath)) {
                        this.initError = true;
                        this.statusMessage = "请配置微信OCR访问地址";
                        return;
                    }
                    this.qrCodeUrl = `${uploadPath}?uploadkey=${this.uploadKey}&certType=${this.certType}`;
                    this.$nextTick(() => this.renderQrCode());
                } else {
                    throw new Error("获取上传标识失败");
                }
            } catch (error) {
                console.error("初始化会话失败:", error);
                this.initError = true;
                this.statusMessage = "加载失败，请重试";
            }
        },

        // 使用 qrcodejs2 渲染二维码 (150x150)
        renderQrCode() {
            const container = this.$refs.qrCodeContainer;
            if (container) {
                container.innerHTML = "";
                this.qrCodeInstance = new QRCode(container, {
                    text: this.qrCodeUrl,
                    width: 150,
                    height: 150,
                    colorDark: "#000000",
                    colorLight: "#ffffff",
                    correctLevel: QRCode.CorrectLevel.H
                });
            }
        },

        // 点击"已完成"按钮 - 带 500ms 防抖
        checkStatus() {
            if (this._checkTimer) clearTimeout(this._checkTimer);
            this._checkTimer = setTimeout(() => this._doCheckStatus(), 500);
        },

        // 执行状态查询
        async _doCheckStatus() {
            this.checking = true;
            this.statusMessage = "";
            try {
                const res = await commonApi.wechatCertUploadQuery({ uploadkey: this.uploadKey });
                if (res.code === "0" && res.data) {
                    const { ocrResult, fileInfoList } = res.data;
                    if (ocrResult || (fileInfoList && fileInfoList.length > 0)) {
                        this.ocrResult = ocrResult;
                        this.fileInfoList = fileInfoList || [];
                        this.$emit("wxSuccess", { ocrResult, fileInfoList: this.fileInfoList });
                        this.handleCancel();
                    } else {
                        // 未找到上传信息，提示用户重新上传
                        this.isExpired = true;
                        this.statusMessage =
                            "未找到上传信息，可能已超过有效期，请刷新二维码后重新上传";
                    }
                } else {
                    // 接口返回异常，也提示过期
                    this.isExpired = true;
                    this.statusMessage = "未找到上传信息，请刷新二维码后重新上传";
                }
            } catch (error) {
                console.error("查询状态失败:", error);
                // 查询失败时，标记为过期，提示用户刷新二维码
                this.isExpired = true;
                this.statusMessage = "未找到上传信息，请刷新二维码后重新上传";
            } finally {
                this.checking = false;
            }
        },

        // 刷新二维码
        refreshQrCode() {
            this.resetWechatState();
            this.initSession();
        },

        // 重试初始化
        retryInit() {
            this.resetWechatState();
            this.initSession();
        },

        // 取消
        handleCancel() {
            this.$emit("cancel");
            this.resetWechatState();
        },

        // 重置微信上传状态
        resetWechatState() {
            this.uploadKey = "";
            this.qrCodeUrl = "";
            this.checking = false;
            this.createTime = 0;
            this.isExpired = false;
            this.statusMessage = "";
            this.initError = false;
            this.ocrResult = null;
            this.fileInfoList = [];
            if (this._checkTimer) clearTimeout(this._checkTimer);
        }
    }
};
</script>

<style lang="scss" scoped>
@import "../../assets/css/varibles";

.wxscan-upload {
    padding: 10px;

    .wxscan-content {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: flex-start;
    }

    // 步骤说明
    .steps-box {
        display: flex;
        justify-content: center;
        width: 100%;
        margin-bottom: 10px;
        gap: 17px;

        .step-item {
            padding: 25px 0;
            background: var(--color-bg-container);
            border-radius: 4px;
            display: flex;
            flex-direction: column;
            align-items: center;
            flex: 1;

            .step-text {
                font-size: 15px;
                color: var(--color-text-primary);
                white-space: nowrap;
                margin-bottom: 20px;
            }
            .step-img-container {
                display: flex;
                align-items: center;
                justify-content: center;
                width: 150px;

                .step-img {
                    width: 100%;
                }

                // 二维码区域
                .qr-section {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 12px;
                    width: 100%;

                    .qr-code-container {
                        position: relative;
                        border: 2px dashed #e0e0e0;
                        border-radius: 8px;
                        background: #fff;

                        .qr-code-box {
                            width: 150px;
                            height: 150px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                        }

                        .qr-mask {
                            position: absolute;
                            top: 0;
                            left: 0;
                            right: 0;
                            bottom: 0;
                            background: rgba(255, 255, 255, 0.95);
                            display: flex;
                            flex-direction: column;
                            align-items: center;
                            justify-content: center;
                            gap: 12px;
                            color: var(--color-primary);
                            font-size: 14px;

                            i {
                                font-size: 36px;
                                animation: rotating 2s linear infinite;
                            }
                        }
                    }

                    .qr-expired-mask {
                        width: 150px;
                        height: 150px;
                        display: flex;
                        flex-direction: column;
                        align-items: center;
                        justify-content: center;
                        gap: 12px;
                        background: #fff;
                        border: 2px dashed #e0e0e0;
                        border-radius: 8px;
                        color: #f56c6c;

                        i {
                            font-size: 52px;
                        }

                        p {
                            font-size: 14px;
                            margin: 0;
                            color: #666;
                        }
                    }

                    .status-message {
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        background: #ecf5ff;
                        border-radius: 4px;
                        color: #409eff;
                        font-size: 12px;
                        max-width: 500px;

                        i {
                            font-size: 18px;
                        }
                    }
                }
            }
        }
    }

    // 初始化失败
    .init-error {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 16px;
        color: #f56c6c;

        i {
            font-size: 56px;
            color: #f56c6c;
            opacity: 0.8;
        }

        p {
            font-size: 15px;
            margin: 0;
            color: #999;
        }

        .el-button {
            margin-top: 8px;
        }
    }

    // 按钮组
    .btn-group {
        margin-top: 30px;
        display: flex;
        justify-content: center;
        gap: 20px;

        .dc-cancel-btn {
            width: 160px;
            height: 46px;
            font-size: 15px;
        }

        .confirm {
            width: 320px;
            height: 46px;
            font-size: 15px;
            font-weight: 500;
        }
    }
}

@keyframes rotating {
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
}
</style>
